/**
 * Card-on-File Types
 *
 * Types for storing and charging customer payment methods.
 * Used with Stripe SetupIntent for secure card storage.
 *
 * Note: Field names match the Postman collection (camelCase from API).
 */

/**
 * Stored payment method for a client (from API response)
 */
export interface ClientPaymentMethod {
  id: number;
  uuid: string;
  stripePaymentMethodId: string;
  nickname?: string | null;
  cardBrand: string;
  cardLast4: string;
  cardExpMonth: number;
  cardExpYear: number;
  isDefault: boolean;
}

/**
 * Request to create a SetupIntent for adding a card
 */
export interface CreateSetupIntentRequest {
  clientId: string;
  salonId: string;
}

/**
 * Response from creating a SetupIntent
 */
export interface CreateSetupIntentResponse {
  clientSecret: string;
  setupIntentId: string;
}

/**
 * Response from getting client payment methods
 */
export interface GetClientPaymentMethodsResponse {
  paymentMethods: ClientPaymentMethod[];
  count: number;
}

/**
 * Response from setting a client's default payment method
 */
export interface SetDefaultClientPaymentMethodResponse {
  defaultPaymentMethodId: number;
}

/**
 * Response from setting/clearing a payment method nickname
 */
export interface SetClientPaymentMethodNicknameResponse {
  paymentMethodId: number;
  nickname: string | null;
}

/**
 * Request to charge a card on file.
 *
 * - Provide either `amountCents` OR `percentage`, not both.
 * - `tipCents` is the portion of `amountCents` that should be recorded as tip
 *   and distributed across booked-service line items. Must satisfy
 *   `0 <= tipCents <= amountCents`. Omit (or send 0) when no tip.
 */
export interface ChargeCardOnFileRequest {
  transactionId: string;
  amountCents?: number;
  percentage?: number;
  paymentMethodId?: number;
  tipCents?: number;
}

/**
 * Response from charging a card on file
 */
export interface ChargeCardOnFileResponse {
  status: string;
  paymentIntentId: string;
  clientSecret?: string;
  payment?: {
    id: string;
    method: string;
    type: string;
    status: string;
    stripe_payment_intent_id: string;
    amount: number;
    created_dt: string;
    updated_dt: string;
    transaction_id: string;
  };
  transaction?: {
    id: string;
    transaction_status: string;
    appointment_status?: string;
  };
}

/**
 * Response from deleting a payment method
 * Returns 204 No Content on success
 */
export interface DeletePaymentMethodResponse {
  message?: string;
}

/**
 * Helper to check if a card is valid for a given date
 */
export function isCardValidForDate(
  card: ClientPaymentMethod,
  date: Date,
): boolean {
  const expDate = new Date(card.cardExpYear, card.cardExpMonth, 0);
  return expDate >= date;
}

/**
 * Helper to check if a card is expired as of "today"
 */
export function isCardExpired(card: ClientPaymentMethod): boolean {
  return !isCardValidForDate(card, new Date());
}

/**
 * Helper to format card display string
 */
export function formatCardDisplay(card: ClientPaymentMethod): string {
  const month = card.cardExpMonth.toString().padStart(2, "0");
  const year = card.cardExpYear.toString().slice(-2);
  return `${card.cardBrand} •••• ${card.cardLast4} (${month}/${year})`;
}
